import { SettingOutlined } from '@ant-design/icons';
import { Button, Popover, Select, Tooltip, Typography } from 'antd';
import { useCallback, useMemo, useState } from 'react';

import { useCloseFloatingOnScroll } from '@app/shared/components';

import type { LeadsRegistryLoadBatchSize } from '../../../../model/types';
import type { FC } from 'react';

import './LeadsRegistryDisplaySettings.scss';

const { Text } = Typography;

export interface LeadsRegistryDisplaySettingsProps {
  loadBatchSize: LeadsRegistryLoadBatchSize;
  loadBatchSizeOptions: LeadsRegistryLoadBatchSize[];
  onLoadBatchSizeChange(value: LeadsRegistryLoadBatchSize): void;
  // Funnels-only: maximum number of stage columns shown at once. Omitted on the
  // registry view, where the control is hidden.
  maxVisibleColumns?: number;
  maxVisibleColumnsOptions?: number[];
  onMaxVisibleColumnsChange?(value: number): void;
}

export const LeadsRegistryDisplaySettings: FC<
  LeadsRegistryDisplaySettingsProps
> = ({
  loadBatchSize,
  loadBatchSizeOptions,
  onLoadBatchSizeChange,
  maxVisibleColumns,
  maxVisibleColumnsOptions,
  onMaxVisibleColumnsChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const closeTooltipHandler = useCallback(() => setIsTooltipOpen(false), []);

  useCloseFloatingOnScroll(isTooltipOpen, closeTooltipHandler);

  const openChangeHandler = useCallback((nextIsOpen: boolean) => {
    setIsOpen(nextIsOpen);

    // The settings popover and the hint tooltip must never overlap.
    if (nextIsOpen) {
      setIsTooltipOpen(false);
    }
  }, []);

  const tooltipOpenChangeHandler = useCallback(
    (nextIsOpen: boolean) => {
      setIsTooltipOpen(nextIsOpen && !isOpen);
    },
    [isOpen]
  );

  const batchSizeOptions = useMemo(
    () =>
      loadBatchSizeOptions.map((value) => ({
        label: value,
        value,
      })),
    [loadBatchSizeOptions]
  );

  const columnOptions = useMemo(
    () =>
      (maxVisibleColumnsOptions ?? []).map((value) => ({
        label: value,
        value,
      })),
    [maxVisibleColumnsOptions]
  );

  const hasColumnsSetting =
    maxVisibleColumns !== undefined &&
    onMaxVisibleColumnsChange !== undefined &&
    columnOptions.length > 0;

  const content = (
    <div className="LeadsRegistryDisplaySettings__content">
      <div className="LeadsRegistryDisplaySettings__group">
        <div className="LeadsRegistryDisplaySettings__group-header">
          <Text className="LeadsRegistryDisplaySettings__title">
            Количество лидов для дозагрузки
          </Text>
          <Text className="LeadsRegistryDisplaySettings__description">
            Размер порции при прокрутке вниз.
          </Text>
        </div>

        <Select<LeadsRegistryLoadBatchSize>
          size="middle"
          value={loadBatchSize}
          options={batchSizeOptions}
          className="LeadsRegistryDisplaySettings__select"
          popupMatchSelectWidth={false}
          onChange={onLoadBatchSizeChange}
        />
      </div>

      {hasColumnsSetting && (
        <div className="LeadsRegistryDisplaySettings__group">
          <div className="LeadsRegistryDisplaySettings__group-header">
            <Text className="LeadsRegistryDisplaySettings__title">
              Максимум колонок
            </Text>
            <Text className="LeadsRegistryDisplaySettings__description">
              Сколько этапов воронки показывать одновременно.
            </Text>
          </div>

          <Select<number>
            size="middle"
            value={maxVisibleColumns}
            options={columnOptions}
            className="LeadsRegistryDisplaySettings__select"
            popupMatchSelectWidth={false}
            onChange={onMaxVisibleColumnsChange}
          />
        </div>
      )}
    </div>
  );

  return (
    <div className="LeadsRegistryDisplaySettings">
      <Popover
        trigger="click"
        placement="leftBottom"
        open={isOpen}
        content={content}
        classNames={{ root: 'LeadsRegistryDisplaySettings__popover' }}
        onOpenChange={openChangeHandler}
      >
        <Tooltip
          title="Настройки"
          placement="left"
          open={isTooltipOpen}
          destroyOnHidden
          onOpenChange={tooltipOpenChangeHandler}
        >
          <Button
            icon={<SettingOutlined />}
            className="LeadsRegistryDisplaySettings__trigger"
            aria-label="Настройки"
          />
        </Tooltip>
      </Popover>
    </div>
  );
};
